// Mobile-nav logic (issue #401). Maps the current route to a domain context
// and the single active tab, and produces the per-context bar items and header
// title. Framework-light and side-effect-free (it references lucide-react icon
// components as plain values but renders no JSX and touches no DOM), so it is
// unit-tested in isolation.

import { Home, Wallet, LayoutDashboard, Receipt, FileText, BarChart3, ListChecks, CalendarDays } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TFunction } from 'i18next';

export type MobileNavContext = 'hub' | 'finance' | 'household';

// Finance-context routes. The first four also have their own bottom-bar tab;
// the rest anchor to Dashboard (/finance).
const FINANCE_TAB_ROUTES = ['/finance', '/expenses', '/bills', '/reports'];
const FINANCE_OTHER_ROUTES = ['/investments', '/search', '/categories', '/accounts'];

export function resolveMobileNav(pathname: string): {
  context: MobileNavContext;
  activeTab: string;
} {
  if (pathname === '/' || pathname.startsWith('/settings')) {
    return { context: 'hub', activeTab: '/' };
  }
  if (pathname.startsWith('/agenda')) {
    return { context: 'household', activeTab: '/agenda' };
  }
  if (pathname.startsWith('/lists')) {
    return { context: 'household', activeTab: '/lists' };
  }
  const ownTab = FINANCE_TAB_ROUTES.find((r) => pathname.startsWith(r));
  if (ownTab) {
    return { context: 'finance', activeTab: ownTab };
  }
  if (FINANCE_OTHER_ROUTES.some((r) => pathname.startsWith(r))) {
    return { context: 'finance', activeTab: '/finance' };
  }
  return { context: 'hub', activeTab: '/' };
}

export interface MobileNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

export function getMobileNavItems(context: MobileNavContext, t: TFunction): MobileNavItem[] {
  const home: MobileNavItem = { path: '/', label: t('nav.home'), icon: Home };
  switch (context) {
    case 'finance':
      return [
        home,
        { path: '/finance', label: t('nav.dashboard'), icon: LayoutDashboard },
        { path: '/expenses', label: t('nav.expenses'), icon: Receipt },
        { path: '/bills', label: t('nav.bills'), icon: FileText },
        { path: '/reports', label: t('nav.reports'), icon: BarChart3 },
      ];
    case 'household':
      return [
        home,
        { path: '/lists', label: t('nav.lists'), icon: ListChecks },
        { path: '/agenda', label: t('nav.agenda'), icon: CalendarDays },
      ];
    case 'hub':
    default:
      return [
        home,
        { path: '/finance', label: t('nav.group.finance'), icon: Wallet },
        { path: '/lists', label: t('nav.lists'), icon: ListChecks },
        { path: '/agenda', label: t('nav.agenda'), icon: CalendarDays },
      ];
  }
}

// Brand name shown on the hub; section name elsewhere. Not translated for the
// hub — "Nomi" is the product name.
export function getMobileHeaderTitle(context: MobileNavContext, pathname: string, t: TFunction): string {
  if (pathname.startsWith('/settings')) return t('nav.settings');
  switch (context) {
    case 'finance':
      return t('nav.group.finance');
    case 'household':
      return pathname.startsWith('/agenda') ? t('nav.agenda') : t('nav.lists');
    case 'hub':
    default:
      return 'Nomi';
  }
}
